import { z } from "zod";
import { createEndpoint, createEndpointWithPagination, StandardSuccessResponseSchema } from "../createEndpoint";
import type { HttpClient, QueryParams } from "../httpClient";
import {
    BanRuleSchema,
    BanRuleTestResultSchema,
    BannedEmailSchema,
    BannedIpSchema,
    CreateBanRuleRequestSchema,
    CreateBannedEmailRequestSchema,
    CreateBannedIpRequestSchema,
    TestBanRuleRequestSchema,
    UpdateBanRuleRequestSchema,
} from "../schemas/ban-list";

export interface AdminBanRulesListQueryParams extends QueryParams {
    /** Registered matcher type, e.g. `project_text`. */
    type?: string;
    is_active?: boolean;
    /** Matches rule name and terms. */
    search?: string;
    limit?: number;
    cursor?: string;
}

export interface AdminBannedEmailsListQueryParams extends QueryParams {
    /** Matches the email address. */
    search?: string;
    ban_rule_id?: number;
    source?: "rule" | "manual";
    limit?: number;
    cursor?: string;
}

export interface AdminBannedIpsListQueryParams extends QueryParams {
    /** Matches the IP address or the attached email. */
    search?: string;
    ban_rule_id?: number;
    source?: "rule" | "manual" | "repeat";
    limit?: number;
    cursor?: string;
}

export function createAdminBanListApi(http: HttpClient, baseUrl: string) {
    const banRulesBaseUrl = `${baseUrl}/admin/ban-rules`;
    const bannedEmailsBaseUrl = `${baseUrl}/admin/banned-emails`;
    const bannedIpsBaseUrl = `${baseUrl}/admin/banned-ips`;

    return {
        rules: {
            list: createEndpointWithPagination<
                z.infer<typeof BanRuleSchema>,
                unknown,
                unknown,
                AdminBanRulesListQueryParams
            >(http, "GET", "", {
                base: banRulesBaseUrl,
                itemSchema: BanRuleSchema,
            }),

            get: createEndpoint<
                z.infer<typeof BanRuleSchema>,
                unknown,
                QueryParams,
                { ruleId: number | string }
            >(http, "GET", "/:ruleId", {
                base: banRulesBaseUrl,
                responseSchema: BanRuleSchema,
                params: ["ruleId"],
            }),

            create: createEndpoint<
                z.infer<typeof BanRuleSchema>,
                z.input<typeof CreateBanRuleRequestSchema>
            >(http, "POST", "", {
                base: banRulesBaseUrl,
                responseSchema: BanRuleSchema,
            }),

            update: createEndpoint<
                z.infer<typeof BanRuleSchema>,
                z.input<typeof UpdateBanRuleRequestSchema>,
                QueryParams,
                { ruleId: number | string }
            >(http, "PUT", "/:ruleId", {
                base: banRulesBaseUrl,
                responseSchema: BanRuleSchema,
                params: ["ruleId"],
            }),

            /** Soft delete. Deactivating is the reversible option. */
            delete: createEndpoint<
                z.infer<typeof StandardSuccessResponseSchema>,
                unknown,
                QueryParams,
                { ruleId: number | string }
            >(http, "DELETE", "/:ruleId", {
                base: banRulesBaseUrl,
                responseSchema: StandardSuccessResponseSchema,
                params: ["ruleId"],
            }),

            /** Dry-run against every active rule. Nothing is banned or counted. */
            test: createEndpoint<
                z.infer<typeof BanRuleTestResultSchema>,
                z.input<typeof TestBanRuleRequestSchema>
            >(http, "POST", "/test", {
                base: banRulesBaseUrl,
                responseSchema: BanRuleTestResultSchema,
            }),
        },

        emails: {
            list: createEndpointWithPagination<
                z.infer<typeof BannedEmailSchema>,
                unknown,
                unknown,
                AdminBannedEmailsListQueryParams
            >(http, "GET", "", {
                base: bannedEmailsBaseUrl,
                itemSchema: BannedEmailSchema,
            }),

            get: createEndpoint<
                z.infer<typeof BannedEmailSchema>,
                unknown,
                QueryParams,
                { bannedEmailId: number | string }
            >(http, "GET", "/:bannedEmailId", {
                base: bannedEmailsBaseUrl,
                responseSchema: BannedEmailSchema,
                params: ["bannedEmailId"],
            }),

            /** Manual ban. Also locks the account behind the address, if any. */
            create: createEndpoint<
                z.infer<typeof BannedEmailSchema>,
                z.input<typeof CreateBannedEmailRequestSchema>
            >(http, "POST", "", {
                base: bannedEmailsBaseUrl,
                responseSchema: BannedEmailSchema,
            }),

            /** Unban: drops the entry and restores the account to `regular`. */
            delete: createEndpoint<
                z.infer<typeof StandardSuccessResponseSchema>,
                unknown,
                QueryParams,
                { bannedEmailId: number | string }
            >(http, "DELETE", "/:bannedEmailId", {
                base: bannedEmailsBaseUrl,
                responseSchema: StandardSuccessResponseSchema,
                params: ["bannedEmailId"],
            }),
        },

        /** Registry only — the API never rejects a request because of this list. */
        ips: {
            list: createEndpointWithPagination<
                z.infer<typeof BannedIpSchema>,
                unknown,
                unknown,
                AdminBannedIpsListQueryParams
            >(http, "GET", "", {
                base: bannedIpsBaseUrl,
                itemSchema: BannedIpSchema,
            }),

            get: createEndpoint<
                z.infer<typeof BannedIpSchema>,
                unknown,
                QueryParams,
                { bannedIpId: number | string }
            >(http, "GET", "/:bannedIpId", {
                base: bannedIpsBaseUrl,
                responseSchema: BannedIpSchema,
                params: ["bannedIpId"],
            }),

            create: createEndpoint<
                z.infer<typeof BannedIpSchema>,
                z.input<typeof CreateBannedIpRequestSchema>
            >(http, "POST", "", {
                base: bannedIpsBaseUrl,
                responseSchema: BannedIpSchema,
            }),

            delete: createEndpoint<
                z.infer<typeof StandardSuccessResponseSchema>,
                unknown,
                QueryParams,
                { bannedIpId: number | string }
            >(http, "DELETE", "/:bannedIpId", {
                base: bannedIpsBaseUrl,
                responseSchema: StandardSuccessResponseSchema,
                params: ["bannedIpId"],
            }),
        },
    };
}

export type AdminBanListApi = ReturnType<typeof createAdminBanListApi>;
